import React from 'react';
import {
  Award,
  BadgePlus,
  BookOpenText,
  CreditCard,
  Database,
  DoorClosed,
  DoorOpen,
  FilePlus,
  FolderOpen,
  Home,
  MessageCircle,
  Shield,
  Tag,
  User,
  Wallet,
  XIcon,
} from 'lucide-react';
import { CustomAccordion } from '../custom-accordion';
import './style.scss';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { eraseCookie } from '@/redux-config/hooks';

const CustomSidebar: React.FC<{
  dir: 'ltr' | 'rtl';
}> = ({ dir }) => {
  return (
    <>
      <div
        className={
          ' col-lg-3 col-xl-2 h-100 bg-custom-white overflow-y-auto d-none d-lg-flex flex-column'
        }
        dir={dir === 'ltr' ? 'rtl' : 'ltr'}
      >
        <div dir={dir}>
          <ItemsOfSideBar />
        </div>
      </div>
      <input type={'checkbox'} id={'sidebarCheck'} className={'d-none'} />
      <label
        className={'close-hover d-block d-lg-none'}
        htmlFor={'sidebarCheck'}
      ></label>
      <div
        className={
          'd-flex d-lg-none flex-column bg-custom-white overflow-y-auto h-100 sidebar shadow-sm'
        }
        dir={dir}
      >
        <ItemsOfSideBar />
      </div>
    </>
  );
};

const ItemsOfSideBar: React.FC<{}> = () => {
  const { pathname } = useLocation();

  const { t } = useTranslation();

  return (
    <>
      <div
        className={
          'd-flex flex-row justify-content-between align-items-center px-3'
        }
      >
        <div className={'d-flex flex-row align-items-center  my-2 gap-2'}>
          <span style={{ fontSize: '1.55rem' }} className={'text1 mt-2'}>
            {t('MahdiBookShop')}
          </span>
        </div>
        <label htmlFor={'sidebarCheck'} className={'d-block d-lg-none'}>
          <XIcon className={'g-cursor-pointer'} />
        </label>
      </div>

      <div className={'d-flex flex-column'}>
        <span className={'text4 px-3 mt-2'}>{t('Dashboard')}</span>
        <CustomAccordion
          title={t('Dashboard')}
          icon={<Home />}
          open={pathname == '/' || pathname.indexOf('dashboard') > 0}
        >
          <div className={'d-flex flex-column '}>
            <Link
              to={'/'}
              className={`sidebar-option ${pathname === '/' ? 'text1' : ''}`}
            >
              {t('Dashboard')}
            </Link>
            <Link
              to={'/dashboard/about-us'}
              className={`sidebar-option ${
                pathname.indexOf('about-us') > 0 ? 'text1' : ''
              }`}
            >
              {t('AboutUs')}
            </Link>
          </div>
        </CustomAccordion>
      </div>

      <div className={'d-flex flex-column mt-2'}>
        <span className={'text4 px-3 mt-2'}>{t('Users')}</span>
        <CustomAccordion
          title={t('User')}
          icon={<User />}
          open={pathname.indexOf('user') > 0}
        >
          <div className={'d-flex flex-column '}>
            <Link
              to={'/user'}
              className={`sidebar-option ${
                pathname === '/user' ? 'text1' : ''
              }`}
            >
              {t('List')}
            </Link>
          </div>
        </CustomAccordion>
        <CustomAccordion
          title={t('Admin')}
          icon={<Award />}
          open={pathname.indexOf('admin') > 0}
        >
          <div className={'d-flex flex-column '}>
            <Link
              to={'/admin'}
              className={`sidebar-option ${
                pathname === '/admin' ? 'text1' : ''
              }`}
            >
              {t('List')}
            </Link>
            <Link
              to={'/admin/new'}
              className={`sidebar-option ${
                pathname.indexOf('admin/new') > 0 ? 'text1' : ''
              }`}
            >
              {t('New')}
            </Link>
          </div>
        </CustomAccordion>
        <CustomAccordion
          title={t('Access')}
          icon={<Shield />}
          open={pathname.indexOf('roles') > 0}
        >
          <div className={'d-flex flex-column '}>
            <Link
              to={'/roles'}
              className={`sidebar-option ${
                pathname.indexOf('roles') > 0 ? 'text1' : ''
              }`}
            >
              {t('Roles')}
            </Link>
            <Link
              to={'/roles/permission'}
              className={`sidebar-option ${
                pathname.indexOf('roles/permission') > 0 ? 'text1' : ''
              }`}
            >
              {t('New')}
            </Link>
          </div>
        </CustomAccordion>
      </div>

      <div className={'d-flex flex-column mt-2'}>
        <span className={'text4 px-3 mt-2'}>{t('Options')}</span>
        <Link
          to={'/category'}
          className={`d-flex flex-row gap-2 py-2 align-items-center px-3 text3 ${
            pathname === '/category' ? 'bg-custom-light' : ''
          }`}
        >
          <MessageCircle size={'20px'} />
          <div className={`mt-1`}>{t('Category')}</div>
        </Link>

        <Link
          to={'/publisher'}
          className={`d-flex flex-row gap-2 py-2 align-items-center px-3 text3 ${
            pathname === '/publisher' ? 'bg-custom-light' : ''
          }`}
        >
          <BadgePlus size={'20px'} />
          <div className={`mt-1`}>{t('Publishers')}</div>
        </Link>

        <CustomAccordion
          title={t('Pricing')}
          icon={<Tag />}
          open={pathname.indexOf('pricing') > 0}
        >
          <div className={'d-flex flex-column '}>
            <Link
              to={'/pricing'}
              className={`sidebar-option ${
                pathname === '/pricing' ? 'text1' : ''
              }`}
            >
              {t('List')}
            </Link>
            <Link
              to={'/pricing/new'}
              className={`sidebar-option ${
                pathname.indexOf('pricing/new') > 0 ? 'text1' : ''
              }`}
            >
              {t('New')}
            </Link>
          </div>
        </CustomAccordion>

        <CustomAccordion
          title={t('Products')}
          icon={<BookOpenText />}
          open={pathname.indexOf('product') > 0}
        >
          <div className={'d-flex flex-column '}>
            <Link
              to={'/product'}
              className={`sidebar-option ${
                pathname === '/product' ? 'text1' : ''
              }`}
            >
              {t('List')}
            </Link>
            <Link
              to={'/product/new'}
              className={`sidebar-option ${
                pathname.indexOf('product/new') > 0 ? 'text1' : ''
              }`}
            >
              {t('New')}
            </Link>
          </div>
        </CustomAccordion>
      </div>

      <div className={'d-flex flex-column mt-2'}>
        <span className={'text4 px-3 mt-2'}>{t('Transaction')}</span>
        <CustomAccordion
          title={t('Payment')}
          icon={<CreditCard />}
          open={pathname.indexOf('payment') > 0}
        >
          <div className={'d-flex flex-column '}>
            <Link
              to={'/payment'}
              className={`sidebar-option ${
                pathname.indexOf('wallet') > 0 ? 'text1' : ''
              }`}
            >
              {t('List')}
            </Link>
          </div>
        </CustomAccordion>
      </div>
      <div
        className={`d-flex flex-row gap-2 py-2 align-items-center px-3 text3 exit-part`}
        onClick={() => {
          eraseCookie('user');
          localStorage.clear();
          window.location.replace('/login');
        }}
      >
        <DoorClosed className={'mx-1'} size={'20px'} />
        <DoorOpen className={'mx-1'} size={'20px'} />
        <span className={`mt-1`}>{t('Exit')}</span>
      </div>
    </>
  );
};

export default CustomSidebar;
